import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Paper, CircularProgress, Snackbar, Alert,
  IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../components/PageHeader';
import DepartmentHeader from '../components/DepartmentHeader';
import { useDepartment } from '../context/DepartmentContext';
import { getTeachers, createTeacher, updateTeacher, deleteTeacher } from '../services/api';

const Teachers = () => {
  const { selectedDepartment } = useDepartment();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentTeacher, setCurrentTeacher] = useState({
    name: '',
    email: '',
    phone: '',
    department_id: ''
  });
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Update currentTeacher when selectedDepartment changes
  useEffect(() => {
    if (selectedDepartment) {
      setCurrentTeacher(prev => ({
        ...prev,
        department_id: selectedDepartment.department_id
      }));
    }
  }, [selectedDepartment]);

  const columns = [
    { field: 'teacher_id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton 
            color="primary" 
            size="small"
            onClick={() => handleOpenDialog(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            size="small"
            onClick={() => handleDeleteClick(params.row.teacher_id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDepartment) return;
      
      try {
        setLoading(true);
        const response = await getTeachers();
        
        // Filter teachers by department
        const departmentTeachers = response.data.filter(
          teacher => teacher.department_id === selectedDepartment.department_id
        );
        
        setTeachers(departmentTeachers);
      } catch (error) {
        console.error('Error fetching teachers:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load teachers',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedDepartment]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDialog = (teacher = null) => {
    if (teacher) {
      setCurrentTeacher({
        name: teacher.name,
        email: teacher.email || '',
        phone: teacher.phone || '',
        department_id: selectedDepartment.department_id
      });
      setSelectedId(teacher.teacher_id);
    } else {
      setCurrentTeacher({
        name: '',
        email: '',
        phone: '',
        department_id: selectedDepartment.department_id
      });
      setSelectedId(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTeacher(selectedId);
      setTeachers(teachers.filter(teacher => teacher.teacher_id !== selectedId));
      setSnackbar({
        open: true,
        message: 'Teacher deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting teacher:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete teacher',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTeacher(prev => ({ ...prev, [name]: value }));
    
    // Clear the error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentTeacher.name.trim()) {
      errors.name = 'Teacher name is required';
    }
    // Email validation is optional
    if (currentTeacher.email && !isValidEmail(currentTeacher.email)) {
      errors.email = 'Invalid email format';
    }
    return errors;
  };

  const isValidEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const teacherData = {
        ...currentTeacher,
        department_id: selectedDepartment.department_id
      };

      if (selectedId) {
        // Update existing teacher
        await updateTeacher(selectedId, teacherData);
        setTeachers(teachers.map(teacher => 
          teacher.teacher_id === selectedId 
            ? { ...teacher, ...teacherData, teacher_id: selectedId } 
            : teacher
        ));
        
        setSnackbar({
          open: true,
          message: 'Teacher updated successfully',
          severity: 'success'
        });
      } else {
        // Create new teacher
        const response = await createTeacher(teacherData);
        const newTeacher = {
          ...teacherData,
          teacher_id: response.data.teacher_id
        };
        
        setTeachers([...teachers, newTeacher]);
        
        setSnackbar({
          open: true,
          message: 'Teacher created successfully',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving teacher:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save teacher',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <PageHeader 
        title="Teachers Management" 
        subtitle="Add and manage teachers for courses" 
      />
      
      {/* Department Header */}
      {selectedDepartment && (
        <DepartmentHeader returnPath="/teachers" />
      )}

      <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Teacher
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={teachers}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            getRowId={(row) => row.teacher_id}
            autoHeight
            disableSelectionOnClick
            sx={{ minHeight: 400 }}
          />
        )}
      </Paper>

      {/* Add/Edit Teacher Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Teacher' : 'Add Teacher'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              name="name"
              label="Teacher Name"
              fullWidth
              margin="normal"
              value={currentTeacher.name}
              onChange={handleInputChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={currentTeacher.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <TextField
              name="phone"
              label="Phone"
              fullWidth
              margin="normal"
              value={currentTeacher.phone}
              onChange={handleInputChange}
            />
            <FormControl fullWidth margin="normal" disabled>
              <InputLabel>Department</InputLabel>
              <Select
                name="department_id"
                value={currentTeacher.department_id}
                label="Department"
              >
                <MenuItem value={selectedDepartment?.department_id}>
                  {selectedDepartment?.department_name}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this teacher? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Teachers;
